import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Request } from '../../types';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faCircle, faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import { Form, Input } from '@alxgrn/react-form';
import { put } from '../../utils/api';
import { useAppDispatch } from '../../hooks/store';
import { requestsPut } from '../../store/requestsSlice';
import './RequestRejectOrCancel.css';

type Reason = {
    id: number;
    name_ru: string;
    name_en: string;
};

const LIMIT = 150;

export const reasons: Reason[] = [{
    id: 1,
    name_ru: 'Не подходит категория',
    name_en: 'The category does not fit',
},{
    id: 2,
    name_ru: 'Недостаточно информации',
    name_en: 'Not enough information',
},{
    id: 3,
    name_ru: 'Другая причина',
    name_en: 'Another reason',
}];

type Props = {
    request: Request;
    onClose: () => void;
};

const RequestRejectOrCancel: FC<Props> = ({ request, onClose }) => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const hint = request.status === 'negotiation' ? t('reasons.hint_reject') : t('reasons.hint_cancel');
    const title = request.status === 'negotiation' ? t('reasons.title_reject') : t('reasons.title_cancel');
    const submit = request.status === 'negotiation' ? t('reasons.submit_reject') : t('reasons.submit_cancel');
    const dispatch = useAppDispatch();
    const [ reason, setReason ] = useState<Reason>(reasons[0]);
    const [ message, setMessage ] = useState(reasons[0][name]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState(false);

    const changeStatus = async () => {
        const status = request.status === 'approved' ? 'cancelled' : 'rejected';
        if(request.status === 'approved') {
            if(!window.confirm(t('request.action.cancel_confirm') ?? undefined)) return;
        }
        setIsLoading(true);
        setError('');
        const result = await put('/requests/' + request.id, { status, reason: message });
        if(result.error) {
            setError(result.error.text[lang]);
        }
        if(result.data) {
            dispatch(requestsPut(result.data.request as Request));
            setSuccess(true);
        }
        setIsLoading(false);
    };

    if(error) return <p className='error'>{error}</p>;
    if(success) return <p className='success'>{t('request.success_status')}</p>;
    if(isLoading) return <p>{t('common.loading')}</p>;

    return (
        <div className='RequestRejectOrCancel'>
            <div className='title'>{title}</div>
            {reasons.map(r => <div
                key={r.id}
                className='pointer p2'
                onClick={() => {
                    setReason(r);
                    setMessage(r.id === 3 ? '' : r[name]);
                }}
            >
                    <Icon icon={reason?.id === r.id ? faCircleCheck : faCircle} fixedWidth/>&nbsp;
                    {r[name]}
            </div>)}

            {reason.id === 3 && <Form>
                <Input
                    id='message'
                    type='textarea'
                    value={message}
                    onChange={setMessage}
                    placeholder={hint}
                    limit={LIMIT}
                    bottom={`${message.length}/${LIMIT}`}
                />
            </Form>}

            <div className='Buttons'>
                <span className='Cancel' onClick={() => onClose()}>
                    <Icon icon={faArrowLeft} fixedWidth/> {t('reasons.cancel')}
                </span>
                {message.trim()
                ? <span className='Submit' onClick={() => changeStatus()}>
                    <Icon icon={faTimes} fixedWidth/> {submit}
                </span>
                : <span className='Disabled'>
                    <Icon icon={faTimes} fixedWidth/> {submit}
                </span>}
            </div>
        </div>
	);
};

export default RequestRejectOrCancel;
